import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  listEmploymentHistory, fireEmployeeApi, rehireEmployeeApi, exportEmploymentHistory,
} from "../api";

const ROLES = ["employee", "manager", "hr"];

export default function EmploymentHistory({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [fireTarget, setFireTarget] = useState(null);
  const [fireReason, setFireReason] = useState("");
  const [rehireTarget, setRehireTarget] = useState(null);
  const [rehireForm, setRehireForm] = useState({ position: "", role: "employee", salary: 200000, week_type: 5 });

  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const dateLocale = localeMap[i18n.language] || "ru-RU";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(dateLocale); } catch { return iso; }
  };

  const tenureLabel = (days) => {
    if (!days) return "—";
    const y = Math.floor(days / 365);
    const m = Math.floor((days % 365) / 30);
    const d = days % 30;
    const parts = [];
    if (y) parts.push(t("history.tenure_years", { n: y }));
    if (m) parts.push(t("history.tenure_months", { n: m }));
    if (!y && d) parts.push(t("history.tenure_days", { n: d }));
    if (!parts.length) parts.push(t("history.tenure_days", { n: days }));
    return parts.join(" ");
  };

  const reload = async () => {
    setErr("");
    try { setRows(await listEmploymentHistory(filter)); }
    catch (e) { setErr(e?.response?.data?.detail || t("common.load_error")); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const isAdmin = user?.role === "director" || user?.role === "hr";

  const onFire = async () => {
    if (!fireTarget) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await fireEmployeeApi(fireTarget.user_id, fireReason);
      setMsg(t("history.fired_msg", { name: fireTarget.name }));
      setFireTarget(null); setFireReason("");
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(false); }
  };

  const openRehire = (row) => {
    setRehireTarget(row);
    setRehireForm({
      position: row.position || "",
      role: row.role || "employee",
      salary: row.salary || 200000,
      week_type: row.week_type || 5,
    });
  };
  const onRehire = async () => {
    if (!rehireTarget) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await rehireEmployeeApi(rehireTarget.user_id, {
        ...rehireForm,
        salary: Number(rehireForm.salary),
        week_type: Number(rehireForm.week_type),
      });
      setMsg(t("history.rehired_msg", { name: rehireTarget.name }));
      setRehireTarget(null);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || t("common.error")); }
    finally { setBusy(false); }
  };

  const onExport = async () => {
    setBusy(true); setErr("");
    try { await exportEmploymentHistory(); }
    catch { setErr(t("common.export_error")); }
    finally { setBusy(false); }
  };

  const userActiveStatus = {};
  rows.forEach(r => {
    if (r.status === "active") userActiveStatus[r.user_id] = true;
  });

  return (
    <div data-testid="history-page">
      <div className={`d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 ${embedded ? "justify-content-end" : ""}`}>
        {!embedded && (
          <div>
            <Link to="/employees" className="small text-muted text-decoration-none d-inline-flex align-items-center mb-1"
                  data-testid="history-back-employees">
              <i className="bi bi-arrow-left me-1"></i>{t("nav.employees")}
            </Link>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-clock-history me-2"></i>{t("history.title")}
            </h4>
          </div>
        )}
        <div className="d-flex gap-2 flex-wrap">
          <div className="btn-group btn-group-sm" role="group" data-testid="history-filters">
            <button className={`btn ${filter === "all" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setFilter("all")} data-testid="filter-all">{t("history.filter_all")}</button>
            <button className={`btn ${filter === "active" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setFilter("active")} data-testid="filter-active">{t("history.filter_active")}</button>
            <button className={`btn ${filter === "fired" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setFilter("fired")} data-testid="filter-fired">{t("history.filter_fired")}</button>
          </div>
          <button className="btn btn-success btn-sm" onClick={onExport} disabled={busy}
                  data-testid="export-history-btn">
            <i className="bi bi-file-earmark-excel me-1"></i>{t("history.export_excel")}
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger py-2" data-testid="history-error">{err}</div>}
      {msg && <div className="alert alert-success py-2" data-testid="history-success">{msg}</div>}

      <div className="card card-soft" data-testid="history-list">
        <div className="table-responsive">
          <table className="table align-middle mb-0 tabel-compact">
            <thead className="table-light">
              <tr>
                <th>{t("history.th_name")}</th>
                <th>{t("history.th_email")}</th>
                <th>{t("history.th_position")}</th>
                <th>{t("history.th_role")}</th>
                <th className="text-end">{t("history.th_salary")}</th>
                <th>{t("history.th_hired")}</th>
                <th>{t("history.th_fired")}</th>
                <th>{t("history.th_tenure")}</th>
                <th>{t("history.th_status")}</th>
                <th>{t("history.th_reason")}</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const userIsCurrentlyFired = !userActiveStatus[r.user_id];
                return (
                  <tr key={r.history_id} data-testid={`history-row-${r.history_id}`}>
                    <td className="fw-semibold">{r.name || "—"}</td>
                    <td className="small">{r.email || "—"}</td>
                    <td className="small">{r.position || "—"}</td>
                    <td>{t(`roles.${r.role}`, { defaultValue: r.role })}</td>
                    <td className="text-end">{Math.round(r.salary || 0).toLocaleString(dateLocale)}</td>
                    <td className="small">{fmtDate(r.hired_at)}</td>
                    <td className="small">{fmtDate(r.fired_at)}</td>
                    <td className="small">{tenureLabel(r.tenure_days)}</td>
                    <td>
                      {r.status === "active"
                        ? <span className="badge bg-success">{t("history.status_active")}</span>
                        : <span className="badge bg-secondary">{t("history.status_fired")}</span>}
                    </td>
                    <td className="small text-muted">{r.reason || "—"}</td>
                    {isAdmin && (
                      <td className="text-end">
                        {r.status === "active" && r.role !== "director" && (
                          <button className="btn btn-sm btn-outline-danger" disabled={busy}
                                  onClick={() => { setFireTarget(r); setFireReason(""); }}
                                  data-testid={`fire-${r.user_id}`}>
                            <i className="bi bi-box-arrow-right me-1"></i>{t("history.fire")}
                          </button>
                        )}
                        {r.status === "fired" && userIsCurrentlyFired && (
                          <button className="btn btn-sm btn-outline-primary" disabled={busy}
                                  onClick={() => openRehire(r)}
                                  data-testid={`rehire-${r.user_id}`}>
                            <i className="bi bi-arrow-counterclockwise me-1"></i>{t("history.rehire")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={isAdmin ? 11 : 10} className="text-center text-muted py-4">{t("history.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fire modal */}
      {fireTarget && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="fire-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("history.fire_modal_title")}</h5>
                <button type="button" className="btn-close" onClick={() => setFireTarget(null)}></button>
              </div>
              <div className="modal-body">
                <p dangerouslySetInnerHTML={{ __html: t("history.fire_confirm_text", { name: fireTarget.name, position: fireTarget.position || "—" }) }} />
                <p className="small text-muted">{t("history.fire_warning")}</p>
                <label className="form-label small">{t("history.fire_reason_label")}</label>
                <input className="form-control" value={fireReason}
                       onChange={(e) => setFireReason(e.target.value)}
                       placeholder={t("history.fire_reason_ph")}
                       data-testid="fire-reason-input" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setFireTarget(null)}>{t("common.cancel")}</button>
                <button className="btn btn-danger" onClick={onFire} disabled={busy}
                        data-testid="fire-confirm-btn">
                  <i className="bi bi-box-arrow-right me-1"></i>{t("history.fire")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rehire modal */}
      {rehireTarget && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="rehire-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("history.rehire_modal_title")}</h5>
                <button type="button" className="btn-close" onClick={() => setRehireTarget(null)}></button>
              </div>
              <div className="modal-body">
                <p dangerouslySetInnerHTML={{ __html: t("history.rehire_confirm_text", { name: rehireTarget.name }) }} />
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label small">{t("history.rehire_position")}</label>
                    <input className="form-control" value={rehireForm.position}
                           onChange={(e) => setRehireForm({ ...rehireForm, position: e.target.value })}
                           data-testid="rehire-position" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">{t("history.rehire_role")}</label>
                    <select className="form-select" value={rehireForm.role}
                            onChange={(e) => setRehireForm({ ...rehireForm, role: e.target.value })}
                            data-testid="rehire-role">
                      {ROLES.map(r => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">{t("history.rehire_salary")}</label>
                    <input type="number" className="form-control" value={rehireForm.salary}
                           onChange={(e) => setRehireForm({ ...rehireForm, salary: e.target.value })}
                           data-testid="rehire-salary" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">{t("history.rehire_week")}</label>
                    <select className="form-select" value={rehireForm.week_type}
                            onChange={(e) => setRehireForm({ ...rehireForm, week_type: e.target.value })}>
                      <option value={5}>{t("history.rehire_week_5")}</option>
                      <option value={6}>{t("history.rehire_week_6")}</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setRehireTarget(null)}>{t("common.cancel")}</button>
                <button className="btn btn-primary" onClick={onRehire} disabled={busy}
                        data-testid="rehire-confirm-btn">
                  <i className="bi bi-arrow-counterclockwise me-1"></i>{t("history.rehire")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
